import { 
  BookOpen, Pencil, Trash2, ListChecks, AlertTriangle, Check, ChefHat, Package, Copy, MoreVertical, QrCode, TrendingUp, FileText
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import AllergeniBadges from "../AllergeniBadges";
import { DeltaBadge } from "../StoricoVariazioniDialog";

export const RicettaCard = ({ item, isGestione, isAdmin, getCategoriaNome, formatTempo, onCardClick, onEdit, onDuplica, onDelete, onDeleteProcedura, onVerifica, onToggleTracciabilita, alert, onOpenStorico, onDownloadPdf }) => {
  const navigate = useNavigate();
  const pesoGrammi = item.peso_finale_grammi || Math.round((item.peso_finale_kg || 0) * 1000);
  const tempoStr = formatTempo(item.tempo_preparazione);
  const numSteps = item.procedimento?.length || 0;
  const daVerificare = !item.verificata;
  const isLight = item.is_light === true;

  return (
    <div
      className={`bg-card backdrop-blur-sm border rounded-2xl overflow-hidden ${alert ? 'border-l-4 border-l-amber-500 ' : ''}${
        isAdmin ? (daVerificare ? 'border-amber-500/50 p-4' : 'border-border p-4') : 'border-border p-3'
      }`}
      data-testid={`ricetta-card-${item.id}`}
    >
      {isGestione && (
        <>
          {/* Header: nome + badge + dropdown menu */}
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onCardClick(item)}>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-foreground text-base truncate">{item.nome}</h3>
                {isLight ? (
                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-500 border border-amber-500/30" data-testid={`badge-light-${item.id}`}>LIGHT</span>
                ) : (
                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-500 border border-emerald-500/30" data-testid={`badge-completa-${item.id}`}>
                    {daVerificare ? "DA VERIFICARE" : "COMPLETA"}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{getCategoriaNome(item.categoria_id)}</p>
              {/* Allergeni HACCP — calcolati runtime dagli ingredienti */}
              {!isLight && (
                <div className="mt-1.5">
                  <AllergeniBadges
                    allergeni={item.allergeni}
                    nonVerificati={item.allergeni_non_verificati}
                    calcolabile={item.allergeni_calcolabile !== false}
                  />
                </div>
              )}
            </div>
            
            {/* Prezzo (solo admin) */}
            {!isLight && (
              <div className="text-right flex-shrink-0">
                {item.verificata ? (
                  <>
                    {/* Quando c'è un alert mostriamo il costo LIVE attuale (coerente con il badge) */}
                    <p className="text-base font-bold text-lime-400 cursor-pointer hover:underline"
                      data-testid={`ricetta-costo-kg-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(item); }}
                      title="Vedi storico variazioni costo">
                      {((alert ? alert.costo_attuale : item.costo_per_kg) || 0).toFixed(2)}/kg
                    </p>
                    {alert && (
                      <p className="text-[10px] leading-tight flex items-center justify-end gap-1">
                        <span className="text-muted-foreground line-through">era {(alert.costo_salvato || 0).toFixed(2)}</span>
                        <DeltaBadge delta={alert.delta_pct} />
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-base font-bold text-amber-400 cursor-pointer hover:underline"
                      data-testid={`ricetta-costo-kg-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(item); }}
                      title="Costo stimato al kg (in attesa di verifica peso finale)">
                      ~{((alert ? alert.costo_attuale : item.costo_per_kg) || 0).toFixed(2)}/kg
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-tight">tot ~{(item.costo_totale || 0).toFixed(2)}</p>
                  </>
                )}
                <div className="flex items-center justify-end gap-1.5 mt-0.5">
                  {alert && <DeltaBadge delta={alert.delta_pct} />}
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenStorico && onOpenStorico(item); }}
                    className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-colors"
                    title="Storico variazioni costo"
                    data-testid={`open-history-dialog-btn-${item.id}`}
                  >
                    <TrendingUp className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Context menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-9 w-9 rounded-xl flex items-center justify-center hover:bg-muted/60 active:scale-95 transition-all flex-shrink-0" data-testid={`dropdown-menu-actions-${item.id}`}>
                  <MoreVertical className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => onDuplica(item)} data-testid={`dropdown-item-duplicate-${item.id}`}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplica Ricetta
                </DropdownMenuItem>
                {!isLight && numSteps > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onDownloadPdf && onDownloadPdf(item)} data-testid={`dropdown-item-pdf-${item.id}`}>
                      <FileText className="h-4 w-4 mr-2" />
                      Scarica ricetta PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDeleteProcedura(item.id)} className="text-destructive focus:text-destructive" data-testid={`dropdown-item-delete-procedure-${item.id}`}>
                      <ListChecks className="h-4 w-4 mr-2" />
                      Elimina Procedura
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(item.id)} className="text-destructive focus:text-destructive" data-testid={`dropdown-item-delete-recipe-${item.id}`}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Elimina Ricetta
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Alert food cost ancorato alla ricetta */}
          {alert && (
            <div
              className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mt-3 cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => onOpenStorico && onOpenStorico(item)}
              data-testid={`recipe-card-alert-banner-${item.id}`}
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-amber-800 flex-1">
                Costo <b>{(alert.delta_pct || 0) >= 0 ? "+" : ""}{(alert.delta_pct || 0).toFixed(1)}%</b>: da €{(alert.costo_salvato || 0).toFixed(2)} a €{(alert.costo_attuale || 0).toFixed(2)}/kg
              </span>
              <TrendingUp className="h-4 w-4 text-amber-600 flex-shrink-0" />
            </div>
          )}

          {/* Banner "Da verificare" */}
          {!isLight && daVerificare && (
            <div 
              className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5 mt-3 cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => onVerifica(item)}
              data-testid={`verifica-badge-${item.id}`}
            >
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span className="text-xs text-amber-500 font-semibold flex-1">Verifica peso dopo cottura</span>
              <Check className="h-4 w-4 text-amber-500" />
            </div>
          )}

          {/* Toggle Lotti */}
          {!isLight && (
            <div
              className={`flex items-center justify-between gap-3 px-3 py-3 mt-2 rounded-xl border cursor-pointer transition-colors ${
                item.richiede_tracciabilita
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-muted/40 border-border/50'
              }`}
              onClick={() => onToggleTracciabilita(item.id, item.richiede_tracciabilita)}
              data-testid={`toggle-lotti-${item.id}`}
            >
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <QrCode className={`h-4 w-4 mt-0.5 flex-shrink-0 ${item.richiede_tracciabilita ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-xs font-semibold leading-tight ${item.richiede_tracciabilita ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                    Tracciabilità lotti {item.richiede_tracciabilita ? 'attiva' : 'disattiva'}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                    {item.richiede_tracciabilita
                      ? 'Puoi creare lotti di produzione per questa ricetta'
                      : 'Attiva per poter creare lotti di produzione'}
                  </p>
                </div>
              </div>
              {/* Toggle pill CSS nativo */}
              <div
                className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${
                  item.richiede_tracciabilita ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
                onClick={e => { e.stopPropagation(); onToggleTracciabilita(item.id, item.richiede_tracciabilita); }}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  item.richiede_tracciabilita ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </div>
            </div>
          )}
          {isLight && (
            <div className="flex items-center justify-between py-3 mt-2 border-t border-border/50">
              <span className="text-xs text-amber-500 font-medium">Lotti sempre attivi</span>
            </div>
          )}

          {/* Primary actions: grid 2 colonne */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button onClick={() => onEdit(item)} className="h-11 text-sm gap-2 font-semibold bg-primary text-white hover:bg-primary/90 active:scale-95" data-testid={`edit-ricetta-${item.id}`}>
              <Pencil className="h-4 w-4" /> Ingredienti
            </Button>
            {!isLight ? (
              <Button 
                onClick={() => navigate(`/anagrafica/ricette/${item.id}/procedura`)} 
                className="h-11 text-sm gap-2 font-semibold bg-purple-600 text-white hover:bg-purple-700 active:scale-95"
                data-testid={`procedura-ricetta-${item.id}`}
              >
                <ListChecks className="h-4 w-4" /> Procedura
              </Button>
            ) : (
              <div />
            )}
          </div>
        </>
      )}

      {!isGestione && (
        <div>
          {!isLight && daVerificare && (
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3 cursor-pointer" onClick={() => onVerifica(item)} data-testid={`verifica-badge-${item.id}`}>
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span className="text-xs text-amber-500 font-medium flex-1 truncate">Da verificare</span>
              <Check className="h-3.5 w-3.5 text-amber-500" />
            </div>
          )}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-foreground text-base truncate">{item.nome}</h3>
            {isLight ? (
              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-500 border border-amber-500/30">LIGHT</span>
            ) : (
              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-500 border border-emerald-500/30">COMPLETA</span>
            )}
            {!isLight && item.richiede_tracciabilita && (
              <span className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 text-blue-600 border border-blue-400/30">
                <QrCode className="h-2.5 w-2.5" />Lotti
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-3">{getCategoriaNome(item.categoria_id)}</p>
          <div className="flex gap-2">
            <button onClick={() => onCardClick(item)} className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl bg-blue-600 text-white shadow-md hover:bg-blue-700 active:scale-95 transition-all" data-testid={`btn-ingredienti-${item.id}`}>
              <Package className="h-5 w-5" />
              <span className="text-[11px] font-bold">Ingredienti</span>
            </button>
            {!isLight && (
              <button onClick={() => numSteps > 0 ? navigate(`/anagrafica/ricette/${item.id}/procedura?view=1`) : null} className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl shadow-md active:scale-95 transition-all ${numSteps > 0 ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-muted/50 border border-border text-muted-foreground/40 cursor-default shadow-none'}`} disabled={!numSteps} data-testid={`btn-preparazione-${item.id}`}>
                <BookOpen className="h-5 w-5" />
                <span className="text-[11px] font-bold">Preparazione</span>
              </button>
            )}
            {item.richiede_tracciabilita && (
              <button onClick={() => navigate(`/lotti?ricetta_id=${item.id}`)} className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl bg-emerald-600 text-white shadow-md hover:bg-emerald-700 active:scale-95 transition-all" data-testid={`btn-crea-lotto-${item.id}`}>
                <ChefHat className="h-5 w-5" />
                <span className="text-[11px] font-bold">Crea Lotto</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
